import React, { useEffect, useMemo, useState } from "react";
import { AdminPageHead, AdminTabs } from '../brand/admin/admin-dashboard/components/AdminPageHead';
import { useSelector } from "react-redux";
import {
  Box,
  Tabs,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Switch,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutline";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { AdminPublisherService } from "../../core/services";
import { StorefrontService } from "../../core/services/storefront.service";

// Shares the influencer-facing Affiliate Deals module's design tokens
// (src/pages/influencer/cashkaro/CashkaroLayout) instead of a separate
// palette — this page is the internal/admin view of the exact same
// Trackier data, so it should read as the same product.
import { CC as CASHKARO_CC, categoryColor } from "../influencer/cashkaro/CashkaroLayout";

// The Affiliate Deals module's tokens, re-coloured for the admin dashboard
// (the brand flow's look — see admin-dashboard/admin-theme.scss). The
// influencer side keeps the originals.
const CC = {
  ...CASHKARO_CC,
  bg: "#f8fafc",
  border: "#e2e8f0",
  borderHover: "#cbd5e1",
  textPrimary: "#0f172a",
  textSecondary: "#64748b",
  primary: "#66A9DD",
  primaryDark: "#3A7BAD",
  primaryWash: "#EBF4FB",
  font: "inherit",
};

const fieldSx = {
  bgcolor: CC.card,
  "& .MuiOutlinedInput-root": {
    borderRadius: CC.radiusSm,
    fontFamily: CC.font,
    "& fieldset": { borderColor: CC.border },
    "&:hover fieldset": { borderColor: CC.borderHover },
    "&.Mui-focused fieldset": { borderColor: CC.primary },
  },
  "& .MuiInputLabel-root": { fontFamily: CC.font },
};

// Table header row — same plain treatment as the influencer-facing tables
// (CashkaroLinks, CashkaroEarnings): a light background and a 1px border,
// no colored underline.
const tableHeadSx = {
  "& th": {
    bgcolor: CC.bg,
    fontWeight: 700,
    fontFamily: CC.font,
    border: 0,
    borderBottom: `1px solid ${CC.border}`,
    color: CC.textPrimary,
  },
};

// Same treatment as CashkaroEarnings.tsx's SectionPanel header bar.
const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box sx={{ color: CC.primary, display: "flex" }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>{title}</Typography>
  </Box>
);

// Stat tile matching CashkaroEarnings.tsx's StatTile — label+value on the
// left, a soft-tinted icon square on the right, flat border with a hover
// lift, so the Reports tab's KPI strip reads as the same component.
const StatTile: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: CC.radiusLg,
      bgcolor: CC.card,
      border: `1px solid ${CC.border}`,
      transition: ".2s",
      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 20px 40px rgba(15,23,42,.08)" },
    }}
  >
    <Box display="flex" justifyContent="space-between">
      <Box minWidth={0}>
        <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, fontSize: 13 }}>{label}</Typography>
        <Typography sx={{ fontFamily: CC.font, fontSize: 24, fontWeight: 800, mt: 1, color: CC.textPrimary }} noWrap>
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: CC.radiusMd,
          bgcolor: `${color}15`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    </Box>
  </Paper>
);

// A whole panel — title bar (icon + label, its own bottom border) wrapping
// whatever content is passed in — same treatment as CashkaroEarnings.tsx's
// SectionPanel, so "Top by clicks/payout" and the reports table each read as
// one bordered card instead of a heading floating above a separate table.
const SectionPanel: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({ icon, title, children }) => (
  <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 2, md: 3 },
        py: 1.75,
        borderBottom: `1px solid ${CC.border}`,
        bgcolor: CC.bg,
      }}
    >
      <Box sx={{ color: CC.primary, display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Paper>
);

function formatDuration(minutes: number | null | undefined): string | null {
  if (!minutes && minutes !== 0) return null;
  if (minutes % 1440 === 0) return `${minutes / 1440} day${minutes / 1440 === 1 ? "" : "s"}`;
  if (minutes % 60 === 0) return `${minutes / 60} hour${minutes / 60 === 1 ? "" : "s"}`;
  return `${minutes} min`;
}

// ---------------------------------------------------------------- Campaigns ----------------------------------------------------------------

const CampaignsPanel: React.FC<{ userId: number }> = ({ userId }) => {
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await AdminPublisherService.getCampaigns(userId, { search, category });
      const list = res?.data?.campaigns;
      setCampaigns(Array.isArray(list) ? list : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AdminPublisherService.getCategories(userId)
      .then((res) => setCategories(Array.isArray(res?.data?.categories) ? res.data.categories : []))
      .catch(() => setCategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(fetchCampaigns, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, userId]);

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search campaigns"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, ...fieldSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" sx={{ color: CC.textSecondary }} /></InputAdornment> }}
        />
        <TextField
          size="small"
          select
          label="Category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ minWidth: 200, ...fieldSx }}
          InputProps={{ startAdornment: <InputAdornment position="start"><CategoryOutlinedIcon fontSize="small" sx={{ color: CC.textSecondary }} /></InputAdornment> }}
        >
          <MenuItem value="">All categories</MenuItem>
          {categories.map((c) => (
            <MenuItem key={c.trackier_id} value={c.name} sx={{ textTransform: "capitalize" }}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", color: CC.textSecondary, fontFamily: CC.font, fontSize: 13 }}>
          {loading ? "Loading…" : `${campaigns.length} campaign${campaigns.length === 1 ? "" : "s"}`}
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={tableHeadSx}>
                <TableCell>Campaign</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Payout</TableCell>
                <TableCell>Countries</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Priority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, border: 0 }}>
                    <CircularProgress size={24} sx={{ color: CC.primary }} />
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 4, color: CC.textSecondary, border: 0 }}>
                    No campaigns match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((c) => (
                  <TableRow key={c.trackier_campaign_id} hover onClick={() => setSelectedId(c.trackier_campaign_id)} sx={{ cursor: "pointer" }}>
                    <TableCell sx={{ fontWeight: 600, color: CC.textPrimary, display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: CC.radiusSm, bgcolor: "#fff", border: `1px solid ${CC.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                        {c.thumbnail ? (
                          <Box component="img" src={c.thumbnail} alt={c.title} sx={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
                        ) : (
                          <StorefrontOutlinedIcon sx={{ fontSize: 16, color: "#c7cad1" }} />
                        )}
                      </Box>
                      {c.title}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {(c.categories || []).slice(0, 2).map((cat: string) => (
                          <Chip
                            key={cat}
                            label={cat}
                            size="small"
                            sx={{ textTransform: "capitalize", height: 20, fontSize: 11, fontFamily: CC.font, borderRadius: "999px", bgcolor: `${categoryColor(cat)}1a`, color: categoryColor(cat) }}
                          />
                        ))}
                        {(!c.categories || c.categories.length === 0) && <Box sx={{ color: CC.textSecondary }}>—</Box>}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: CC.textSecondary, textTransform: "capitalize" }}>{c.campaign_type?.replace("_", " ") || "—"}</TableCell>
                    <TableCell sx={{ color: CC.accent, fontWeight: 700 }}>{c.payout_summary || "Varies"}</TableCell>
                    <TableCell sx={{ color: CC.textSecondary }}>{(c.countries || []).slice(0, 3).join(", ") || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={c.status}
                        sx={{ height: 20, fontSize: 11, textTransform: "capitalize", borderRadius: "999px", bgcolor: c.status === "active" ? CC.primaryWash : CC.bg, color: c.status === "active" ? CC.primaryDark : CC.textSecondary }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: CC.textSecondary }}>{c.priority ?? "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <CampaignDetailDialog userId={userId} campaignId={selectedId} open={selectedId !== null} onClose={() => setSelectedId(null)} />
    </Box>
  );
};

const CampaignDetailDialog: React.FC<{ userId: number; campaignId: number | null; open: boolean; onClose: () => void }> = ({
  userId,
  campaignId,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [normalized, setNormalized] = useState<any>(null);
  const [raw, setRaw] = useState<any>(null);
  const [accessStatus, setAccessStatus] = useState("none");
  const [requesting, setRequesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !campaignId) return;
    setLoading(true);
    setError(null);
    AdminPublisherService.getCampaignDetail(userId, campaignId)
      .then((res) => {
        const data = res?.data || {};
        setNormalized(data.normalized || null);
        setRaw(data.campaign || null);
        setAccessStatus(data.access_status || "none");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, campaignId]);

  const handleRequestAccess = async () => {
    if (!campaignId) return;
    setRequesting(true);
    setError(null);
    try {
      const res = await AdminPublisherService.requestCampaignAccess(userId, campaignId);
      setAccessStatus(res?.data?.access_status || "pending");
    } catch {
      setError("Could not request access right now — please try again.");
    } finally {
      setRequesting(false);
    }
  };

  const title = normalized?.title;
  const thumbnail = normalized?.thumbnail;
  const trackingWindow = formatDuration(normalized?.tracking_window_minutes);

  // Extra raw fields not already surfaced by normalizeCampaign — dumped so
  // nothing Trackier provides is hidden from the admin view.
  const shownKeys = new Set([
    "id", "title", "description", "kpi", "thumbnail", "categories", "countries", "device", "os", "isps",
    "landing_pages", "tracking_link", "preview_url", "currency", "payouts", "customFields", "model", "app_name",
    "app_id", "status", "HidePayoutPubs", "HidePayoutFromPubsList",
  ]);
  const extraFields = raw ? Object.entries(raw).filter(([k, v]) => !shownKeys.has(k) && v !== null && v !== undefined && v !== "") : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: CC.radiusLg } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary }}>Campaign details</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: CC.textSecondary }}><CloseRoundedIcon fontSize="small" /></IconButton>
      </Box>
      <DialogContent sx={{ pt: 1 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} sx={{ color: CC.primary }} /></Box>
        ) : !normalized ? (
          <Alert severity="warning" sx={{ borderRadius: CC.radiusMd }}>This campaign is no longer available on Trackier.</Alert>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 1.75, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Box sx={{ width: 56, height: 56, borderRadius: CC.radiusMd, bgcolor: "#fff", border: `1px solid ${CC.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
                {thumbnail ? (
                  <Box sx={{ width: "100%", height: "100%", p: 0.75, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Box component="img" src={thumbnail} alt={title} sx={{ maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto", objectFit: "contain" }} />
                  </Box>
                ) : (
                  <StorefrontOutlinedIcon sx={{ fontSize: 24, color: "#c7cad1" }} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1, minWidth: 200 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font }}>{title}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", my: 0.5 }}>
                  {(normalized.categories || []).map((cat: string) => (
                    <Chip key={cat} label={cat} size="small" sx={{ textTransform: "capitalize", height: 20, fontSize: 11, fontFamily: CC.font, bgcolor: `${categoryColor(cat)}1a`, color: categoryColor(cat) }} />
                  ))}
                  <Chip
                    label={accessStatus === "approved" ? "Access approved" : "Access not requested"}
                    size="small"
                    sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: accessStatus === "approved" ? CC.primaryWash : CC.bg, color: accessStatus === "approved" ? CC.primaryDark : CC.textSecondary }}
                  />
                  {normalized.needs_review && (
                    <Chip label="Needs review" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "#fdecea", color: "#b3261e" }} />
                  )}
                </Box>
                {(trackingWindow || normalized.validation_window_days) && (
                  <Typography variant="caption" sx={{ display: "block", color: CC.textSecondary }}>
                    {trackingWindow && `Tracking window: ${trackingWindow}`}
                    {trackingWindow && normalized.validation_window_days ? " · " : ""}
                    {normalized.validation_window_days ? `Validation: ${normalized.validation_window_days} days` : ""}
                    {normalized.campaign_channel && normalized.campaign_channel !== "unknown" ? ` · Channel: ${normalized.campaign_channel}` : ""}
                  </Typography>
                )}
              </Box>
              {accessStatus !== "approved" && (
                <Button
                  disableElevation
                  disabled={requesting}
                  onClick={handleRequestAccess}
                  sx={{ bgcolor: CC.primary, color: "#fff", fontFamily: CC.font, textTransform: "none", fontWeight: 600, borderRadius: CC.radiusSm, "&:hover": { bgcolor: CC.primaryDark } }}
                >
                  {requesting ? "Requesting…" : "Request Access"}
                </Button>
              )}
            </Box>

            {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: CC.radiusMd }}>{error}</Alert>}

            {normalized.description && (
              <Typography variant="body2" sx={{ color: CC.textSecondary, mt: 1.5, maxHeight: 130, overflowY: "auto" }} dangerouslySetInnerHTML={{ __html: normalized.description }} />
            )}

            {normalized.terms_html && (
              <Accordion disableGutters elevation={0} square sx={{ mt: 1, border: `1px solid ${CC.border}`, borderRadius: CC.radiusSm, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: CC.textSecondary }} />}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: CC.textPrimary }}>Campaign terms</Typography>
                </AccordionSummary>
                <AccordionDetails><Typography variant="body2" sx={{ color: CC.textSecondary }} dangerouslySetInnerHTML={{ __html: normalized.terms_html }} /></AccordionDetails>
              </Accordion>
            )}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} sx={{ color: CC.textPrimary, display: "block", mb: 0.5 }}>Payouts (all geos)</Typography>
                <Box sx={{ border: `1px solid ${CC.border}`, borderRadius: CC.radiusSm, overflow: "hidden" }}>
                  <Table size="small">
                    <TableBody>
                      {(raw?.payouts || []).map((p: any, i: number) => (
                        <TableRow key={i}>
                          <TableCell sx={{ color: CC.textSecondary, border: 0 }}>{(p.geo || []).join(", ") || "ALL"}</TableCell>
                          <TableCell sx={{ color: CC.textPrimary, fontWeight: 600, border: 0 }}>
                            {p.payout_model === "percentage" ? `${p.payout}%` : `${p.currency || normalized.currency || ""} ${p.payout}`}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!raw?.payouts || raw.payouts.length === 0) && (
                        <TableRow><TableCell sx={{ color: CC.textSecondary, border: 0 }}>No payout data</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" fontWeight={700} sx={{ color: CC.textPrimary, display: "block", mb: 0.5 }}>Targeting</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: 13, color: CC.textSecondary }}>
                  <span><b style={{ color: CC.textPrimary }}>Device:</b> {(normalized.device || []).join(", ") || "—"}</span>
                  <span><b style={{ color: CC.textPrimary }}>OS:</b> {(normalized.os || []).join(", ") || "—"}</span>
                  <span><b style={{ color: CC.textPrimary }}>ISPs:</b> {(normalized.isps || []).join(", ") || "All"}</span>
                  <span><b style={{ color: CC.textPrimary }}>Landing pages:</b> {(normalized.landing_pages || []).length}</span>
                </Box>
              </Grid>
            </Grid>

            {extraFields.length > 0 && (
              <Accordion disableGutters elevation={0} square sx={{ mt: 1.5, border: `1px solid ${CC.border}`, borderRadius: CC.radiusSm, "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreRoundedIcon sx={{ color: CC.textSecondary }} />}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: CC.textPrimary }}>Other raw fields from Trackier ({extraFields.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, fontSize: 12.5 }}>
                    {extraFields.map(([k, v]) => (
                      <Box key={k} sx={{ display: "flex", gap: 1 }}>
                        <Box component="span" sx={{ color: CC.textSecondary, minWidth: 140, flexShrink: 0 }}>{k}</Box>
                        <Box component="span" sx={{ color: CC.textPrimary, wordBreak: "break-all" }}>{typeof v === "object" ? JSON.stringify(v) : String(v)}</Box>
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, color: CC.textPrimary, border: `1px solid ${CC.border}`, borderRadius: CC.radiusSm, "&:hover": { borderColor: CC.borderHover, bgcolor: CC.bg } }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------- Profile ----------------------------------------------------------------

const ProfilePanel: React.FC<{ userId: number }> = ({ userId }) => {
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AdminPublisherService.getProfile(userId)
      .then((res) => setProfile(res?.data?.profile || null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} sx={{ color: CC.primary }} /></Box>;
  }
  if (!profile) {
    return <Alert severity="error" sx={{ borderRadius: CC.radiusMd }}>Could not load the Trackier publisher profile.</Alert>;
  }

  const fields: Array<{ label: string; value: string }> = [
    { label: "Publisher ID", value: String(profile.id ?? "—") },
    { label: "Name", value: profile.name || "—" },
    { label: "Email", value: profile.email || "—" },
    { label: "Phone", value: profile.phone || "—" },
    { label: "Company", value: profile.company || "—" },
    { label: "Status", value: profile.status || "—" },
    { label: "Hash ID", value: profile.hashId || "—" },
  ];

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Alert severity="info" sx={{ mb: 2, borderRadius: CC.radiusMd }}>
        This is the single shared Trackier account used across the whole platform. Editing isn't wired up yet —
        Trackier's update-profile endpoint hasn't been confirmed against this account, so this view is read-only for now.
      </Alert>
      <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
        {fields.map((f, i) => (
          <Box
            key={f.label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              px: 2.5,
              py: 1.5,
              borderTop: i === 0 ? "none" : `1px solid ${CC.border}`,
            }}
          >
            <Typography variant="body2" sx={{ color: CC.textSecondary }}>{f.label}</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ color: CC.textPrimary, textAlign: "right", wordBreak: "break-all" }}>{f.value}</Typography>
          </Box>
        ))}
      </Paper>
    </Box>
  );
};

// ---------------------------------------------------------------- Reports ----------------------------------------------------------------

const DIMENSION_OPTIONS: { key: string; label: string }[] = [
  { key: "campaign_id,campaign_name", label: "Campaign" },
  { key: "source", label: "Influencer" },
  { key: "brand", label: "Brand" },
  { key: "product", label: "Product" },
  { key: "country", label: "Country" },
  { key: "created", label: "Date" },
];

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const toStr = (d: Date) => d.toISOString().slice(0, 10);
  return { start: toStr(start), end: toStr(end) };
}

// Builds one chart from whatever dimension(s) the admin picked. If `created`
// (Date) is one of them, records are summed per day into a trend line —
// regardless of any other dimension also selected, since a trend is more
// useful than a compound date+category breakdown. Otherwise it's a top-8
// column chart, labeled by joining every selected dimension's value per
// record (so a Campaign+Country selection reads as e.g. "Neemans · IN").
function buildReportChart(dimColumns: string[], records: any[], metric: "clicks" | "payout", label: string, color: string): Highcharts.Options | null {
  if (records.length === 0) return null;

  if (dimColumns.includes("created")) {
    const byDate = new Map<string, number>();
    for (const r of records) {
      const key = (r.created || "").toString().slice(0, 10);
      if (!key) continue;
      byDate.set(key, (byDate.get(key) || 0) + Number(r[metric] || 0));
    }
    const sorted = Array.from(byDate.entries()).sort(([a], [b]) => a.localeCompare(b));
    if (sorted.length === 0) return null;
    return {
      chart: { type: "spline", height: 260, backgroundColor: "transparent" },
      title: { text: undefined },
      credits: { enabled: false },
      xAxis: { categories: sorted.map(([d]) => new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short" })), lineColor: CC.border },
      yAxis: { title: { text: label }, allowDecimals: metric !== "clicks", gridLineColor: "#f0f0f0", min: 0 },
      legend: { enabled: false },
      tooltip: { pointFormat: `<b>{point.y}</b> ${label.toLowerCase()}` },
      plotOptions: { spline: { color, lineWidth: 3, marker: { fillColor: color, radius: 4 } } },
      series: [{ type: "spline", name: label, data: sorted.map(([, v]) => v) }],
    };
  }

  const labeled = records
    .map((r) => ({ name: dimColumns.map((d) => r[d]).filter((v) => v !== undefined && v !== null && v !== "").join(" · ") || "Unknown", y: Number(r[metric] || 0) }))
    .sort((a, b) => b.y - a.y)
    .slice(0, 8);
  if (labeled.length === 0 || labeled.every((r) => r.y === 0)) return null;

  return {
    chart: { type: "column", height: 260, backgroundColor: "transparent" },
    title: { text: undefined },
    credits: { enabled: false },
    xAxis: { categories: labeled.map((r) => r.name), lineColor: CC.border },
    yAxis: { title: { text: label }, allowDecimals: metric !== "clicks", gridLineColor: "#f0f0f0", min: 0 },
    legend: { enabled: false },
    tooltip: { pointFormat: `<b>{point.y}</b> ${label.toLowerCase()}` },
    plotOptions: { column: { color, borderRadius: 4, pointPadding: 0.15, borderWidth: 0 } },
    series: [{ type: "column", name: label, data: labeled.map((r) => r.y) }],
  };
}

const ReportsPanel: React.FC<{ userId: number }> = ({ userId }) => {
  const [range, setRange] = useState(defaultRange());
  const [dims, setDims] = useState<string[]>(["campaign_id,campaign_name"]);
  const [records, setRecords] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [group, setGroup] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const toggleDim = (key: string) => {
    setDims((prev) => (prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]));
  };

  useEffect(() => {
    setLoading(true);
    const groupKeys = dims.flatMap((d) => d.split(","));
    AdminPublisherService.getReports(userId, { start: range.start, end: range.end, group: groupKeys.length ? groupKeys : undefined })
      .then((res) => {
        setRecords(Array.isArray(res?.data?.records) ? res.data.records : []);
        setSummary(res?.data?.summary || null);
        setGroup(Array.isArray(res?.data?.group) ? res.data.group : []);
      })
      .catch(() => {
        setRecords([]);
        setSummary(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, dims.join(",")]);

  // Only show columns for dimensions the admin actually picked — `group`
  // from the backend may include an extra padding key (see Publisher.getReports)
  // when only one real dimension was selected; that padding key isn't hidden
  // since it's still real, queried data — just not the primary ask.
  const dimColumns = useMemo(() => group, [group]);
  const clicksChart = useMemo(() => buildReportChart(dimColumns, records, "clicks", "Clicks", CC.primary), [dimColumns, records]);
  const payoutChart = useMemo(() => buildReportChart(dimColumns, records, "payout", "Payout (₹)", "#16A34A"), [dimColumns, records]);
  const isTrend = dimColumns.includes("created");

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField label="From" type="date" size="small" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <TextField label="To" type="date" size="small" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {DIMENSION_OPTIONS.map((opt) => {
            const active = dims.includes(opt.key);
            return (
              <Chip
                key={opt.key}
                label={opt.label}
                size="small"
                className={active ? "btn btn-primary btn-sm" : "btn btn-outline-primary btn-sm"}
                onClick={() => toggleDim(opt.key)}
                sx={{
                  fontWeight: 600,
                  fontFamily: CC.font,
                  border: `1px solid ${active ? CC.textPrimary : CC.border}`,
                  bgcolor: active ? CC.textPrimary : CC.card,
                  color: active ? CC.card : CC.textPrimary,
                  "&:hover": { bgcolor: active ? CC.textPrimary : CC.bg },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} sx={{ color: CC.primary }} /></Box>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatTile label="Clicks" value={String(summary?.clicks ?? 0)} icon={<TouchAppOutlinedIcon fontSize="small" />} color={CC.primary} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Approved Conversions" value={String(summary?.approved_conversions ?? 0)} icon={<ShoppingCartOutlinedIcon fontSize="small" />} color="#7C3AED" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Sale Amount" value={`₹${Number(summary?.sale_amount || 0).toFixed(2)}`} icon={<PaidOutlinedIcon fontSize="small" />} color="#0EA5E9" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Payout" value={`₹${Number(summary?.payout || 0).toFixed(2)}`} icon={<CheckCircleOutlineOutlinedIcon fontSize="small" />} color="#16A34A" />
            </Grid>
          </Grid>

          <SectionPanel icon={<BarChartOutlinedIcon fontSize="small" />} title={isTrend ? "Performance trend" : "Performance breakdown"}>
            {!clicksChart && !payoutChart ? (
              <Typography sx={{ color: CC.textSecondary, fontFamily: CC.font, py: 4, textAlign: "center" }}>
                No data in this date range yet.
              </Typography>
            ) : (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={3}>
                  {clicksChart && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: CC.textPrimary, mb: 0.5, fontFamily: CC.font }}>
                        {isTrend ? "Clicks trend" : "Top by clicks"}
                      </Typography>
                      <HighchartsReact highcharts={Highcharts} options={clicksChart} />
                    </Grid>
                  )}
                  {payoutChart && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight={700} sx={{ color: CC.textPrimary, mb: 0.5, fontFamily: CC.font }}>
                        {isTrend ? "Payout trend" : "Top by payout"}
                      </Typography>
                      <HighchartsReact highcharts={Highcharts} options={payoutChart} />
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            <Box sx={{ overflowX: "auto", borderTop: clicksChart || payoutChart ? `1px solid ${CC.border}` : "none" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx}>
                    {dimColumns.map((d) => (
                      <TableCell key={d} sx={{ textTransform: "capitalize" }}>{d.replace(/_/g, " ")}</TableCell>
                    ))}
                    <TableCell align="right">Clicks</TableCell>
                    <TableCell align="right">Approved Conversions</TableCell>
                    <TableCell align="right">Sale Amount</TableCell>
                    <TableCell align="right">Payout</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                  {records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={dimColumns.length + 4} sx={{ textAlign: "center", py: 4, color: CC.textSecondary, border: 0 }}>
                        No data in this date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    records.map((r, i) => (
                      <TableRow key={i} hover>
                        {dimColumns.map((d) => (
                          <TableCell key={d} sx={{ color: CC.textPrimary }}>{String(r[d] ?? "—")}</TableCell>
                        ))}
                        <TableCell align="right">{r.clicks ?? 0}</TableCell>
                        <TableCell align="right">{r.approvedConversions ?? 0}</TableCell>
                        <TableCell align="right">{Number(r.saleAmount || 0).toFixed(2)}</TableCell>
                        <TableCell align="right">{Number(r.payout || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </SectionPanel>
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------- Conversions ----------------------------------------------------------------

const STATUS_COLOR: Record<string, "success" | "warning" | "default" | "error"> = {
  approved: "success",
  pending: "warning",
  cancelled: "error",
  rejected: "error",
};

const STATUS_OPTIONS = ["", "approved", "pending", "cancelled", "rejected"];

// Row-level conversion log, account-wide (no influencer scoping) — the one
// view ReportsPanel's aggregates can't give you: exactly which conversion,
// for which influencer, on which campaign. Backed by trackier_conversions
// (Publisher.getConversions), paginated against our own table.
const ConversionsPanel: React.FC<{ userId: number }> = ({ userId }) => {
  const [range, setRange] = useState(defaultRange());
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [conversions, setConversions] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 50;

  useEffect(() => {
    setLoading(true);
    AdminPublisherService.getConversions(userId, { start: range.start, end: range.end, status: status || undefined, page, limit })
      .then((res) => {
        setConversions(Array.isArray(res?.data?.conversions) ? res.data.conversions : []);
        setTotal(Number(res?.data?.pagination?.total || 0));
      })
      .catch(() => {
        setConversions([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end, status, page]);

  useEffect(() => setPage(1), [range.start, range.end, status]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <TextField label="From" type="date" size="small" value={range.start} onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <TextField label="To" type="date" size="small" value={range.end} onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <TextField label="Status" select size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140, ...fieldSx }}>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s || "all"} value={s} sx={{ textTransform: "capitalize" }}>
              {s || "All statuses"}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={26} sx={{ color: CC.primary }} /></Box>
      ) : (
        <>
          <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={tableHeadSx}>
                    <TableCell>Date</TableCell>
                    <TableCell>Campaign</TableCell>
                    <TableCell>Influencer</TableCell>
                    <TableCell>Goal</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Payout</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conversions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: CC.textSecondary, border: 0 }}>
                        No conversions in this date range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    conversions.map((c) => (
                      <TableRow key={c.trackier_conversion_id} hover>
                        <TableCell sx={{ color: CC.textSecondary }}>
                          {c.trackier_created_at ? new Date(c.trackier_created_at).toLocaleDateString() : "—"}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: CC.textPrimary }}>{c.campaign_name || `Campaign #${c.campaign_id}`}</TableCell>
                        <TableCell sx={{ color: CC.textPrimary }}>
                          {c.influencer_name || (c.influencer_id ? `#${c.influencer_id}` : "—")}
                        </TableCell>
                        <TableCell sx={{ color: CC.textSecondary }}>{c.goal_name || "—"}</TableCell>
                        <TableCell>
                          <Chip size="small" label={c.status} color={STATUS_COLOR[c.status] || "default"} sx={{ height: 22, fontSize: 11, textTransform: "capitalize" }} />
                        </TableCell>
                        <TableCell align="right">{c.currency || ""} {Number(c.payout || 0).toFixed(2)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>

          {total > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: CC.textSecondary }}>
                {total} conversion{total === 1 ? "" : "s"} · page {page} of {totalPages}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} sx={{ textTransform: "none" }}>
                  Previous
                </Button>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} sx={{ textTransform: "none" }}>
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ---------------------------------------------------------------- Brand Access ----------------------------------------------------------------

// Two things live here: (1) the manual per-brand Affiliate Deals access flag — deliberately
// not a subscription entitlement, an admin flips it per brand (see
// 2026-09-13_brand_deals_permission_and_link_requests.sql) — and (2) reviewing the campaign
// link requests that flag unlocks. Trackier has no advertiser/ownership field, so this
// review step is the only real check that a brand is linking a campaign that's actually
// theirs (see Storefront.requestCashkaroCampaignLink).
const BrandAccessPanel: React.FC<{ userId: number }> = ({ userId }) => {
  const [storefronts, setStorefronts] = useState<any[]>([]);
  const [storeSearch, setStoreSearch] = useState("");
  const [storesLoading, setStoresLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const [requests, setRequests] = useState<any[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [actingOnId, setActingOnId] = useState<number | null>(null);
  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const loadStorefronts = () => {
    setStoresLoading(true);
    StorefrontService.adminGetStorefronts(userId, storeSearch || undefined)
      .then((res: any) => setStorefronts(Array.isArray(res?.data?.storefronts) ? res.data.storefronts : []))
      .catch(() => setStorefronts([]))
      .finally(() => setStoresLoading(false));
  };

  const loadRequests = () => {
    setRequestsLoading(true);
    AdminPublisherService.getCampaignLinkRequests(userId, "pending")
      .then((res: any) => setRequests(Array.isArray(res?.data?.requests) ? res.data.requests : []))
      .catch(() => setRequests([]))
      .finally(() => setRequestsLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(loadStorefronts, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeSearch, userId]);

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const toggleBrandDeals = async (storefront_id: number, next: boolean) => {
    setTogglingId(storefront_id);
    setStorefronts((prev) => prev.map((s) => (s.storefront_id === storefront_id ? { ...s, brand_deals_enabled: next ? "True" : "False" } : s)));
    try {
      const res = await StorefrontService.adminSetBrandDealsEnabled(userId, storefront_id, next);
      if (res?.status !== 1) {
        setStorefronts((prev) => prev.map((s) => (s.storefront_id === storefront_id ? { ...s, brand_deals_enabled: next ? "False" : "True" } : s)));
      }
    } catch {
      setStorefronts((prev) => prev.map((s) => (s.storefront_id === storefront_id ? { ...s, brand_deals_enabled: next ? "False" : "True" } : s)));
    } finally {
      setTogglingId(null);
    }
  };

  const approveRequest = async (id: number) => {
    setActingOnId(id);
    try {
      const res = await AdminPublisherService.reviewCampaignLinkRequest(userId, id, "approve");
      if (res?.status === 1) {
        setRequests((prev) => prev.filter((r) => r.id !== id));
      }
    } finally {
      setActingOnId(null);
    }
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    setActingOnId(rejectTarget.id);
    try {
      const res = await AdminPublisherService.reviewCampaignLinkRequest(userId, rejectTarget.id, "reject", rejectReason || undefined);
      if (res?.status === 1) {
        setRequests((prev) => prev.filter((r) => r.id !== rejectTarget.id));
      }
    } finally {
      setActingOnId(null);
      setRejectTarget(null);
      setRejectReason("");
    }
  };

  return (
    <Box>
      <SectionHeader icon={<PlaylistAddCheckOutlinedIcon fontSize="small" />} title="Campaign link requests" />
      {requestsLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: CC.primary }} /></Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden", mb: 4 }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadSx}>
                  <TableCell>Store</TableCell>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell align="right">Review</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 4, color: CC.textSecondary, border: 0 }}>No pending requests.</TableCell>
                  </TableRow>
                ) : (
                  requests.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ fontWeight: 600, color: CC.textPrimary }}>{r.store_name || `Storefront #${r.storefront_id}`}</TableCell>
                      <TableCell sx={{ color: CC.textPrimary }}>{r.cashkaro_campaign_title || `Campaign #${r.cashkaro_campaign_id}`}</TableCell>
                      <TableCell sx={{ color: CC.textSecondary }}>{r.requested_at ? new Date(r.requested_at).toLocaleDateString() : "—"}</TableCell>
                      <TableCell align="right">
                        {actingOnId === r.id ? (
                          <CircularProgress size={18} sx={{ color: CC.primary }} />
                        ) : (
                          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <Button size="small" onClick={() => setRejectTarget(r)} sx={{ textTransform: "none", color: "error.main" }}>
                              Reject
                            </Button>
                            <Button
                              size="small"
                              className={"btn btn-outline-primary btn-sm"}
                
                              variant="contained"
                              disableElevation
                              startIcon={<CheckRoundedIcon fontSize="small" />}
                              onClick={() => approveRequest(r.id)}
                              sx={{ textTransform: "none", fontWeight: 600, bgcolor: CC.textPrimary, "&:hover": { bgcolor: CC.primaryDark } }}
                            >
                              Approve
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <SectionHeader icon={<StorefrontOutlinedIcon fontSize="small" />} title="Affiliate Deals access" />
      <TextField
        size="small"
        placeholder="Search stores by name…"
        value={storeSearch}
        onChange={(e) => setStoreSearch(e.target.value)}
        sx={{ minWidth: 260, mb: 2, ...fieldSx }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" sx={{ color: CC.textSecondary }} /></InputAdornment> }}
      />
      {storesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={24} sx={{ color: CC.primary }} /></Box>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: CC.radiusLg, border: `1px solid ${CC.border}`, bgcolor: CC.card, overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={tableHeadSx}>
                  <TableCell>Store</TableCell>
                  <TableCell>Linked campaign</TableCell>
                  <TableCell align="right">Affiliate Deals access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ "& td": { fontFamily: CC.font, borderColor: CC.border } }}>
                {storefronts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} sx={{ textAlign: "center", py: 4, color: CC.textSecondary, border: 0 }}>No stores match your search.</TableCell>
                  </TableRow>
                ) : (
                  storefronts.map((s) => (
                    <TableRow key={s.storefront_id} hover>
                      <TableCell sx={{ fontWeight: 600, color: CC.textPrimary }}>{s.store_name || `Storefront #${s.storefront_id}`}</TableCell>
                      <TableCell sx={{ color: CC.textSecondary }}>{s.cashkaro_campaign_title || "—"}</TableCell>
                      <TableCell align="right">
                        <Switch
                          size="small"
                          checked={s.brand_deals_enabled === "True"}
                          disabled={togglingId === s.storefront_id}
                          onChange={(e) => toggleBrandDeals(s.storefront_id, e.target.checked)}
                          sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: CC.primary }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: CC.primary } }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: CC.radiusLg } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: CC.textPrimary, fontFamily: CC.font, mb: 1 }}>Reject request</Typography>
          <Typography variant="body2" sx={{ color: CC.textSecondary, fontFamily: CC.font, mb: 2 }}>
            {rejectTarget?.store_name || `Storefront #${rejectTarget?.storefront_id}`} requested "{rejectTarget?.cashkaro_campaign_title || `Campaign #${rejectTarget?.cashkaro_campaign_id}`}".
            Optionally explain why — the brand will see this.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={fieldSx}
          />
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setRejectTarget(null)} sx={{ textTransform: "none", fontWeight: 600, color: CC.textPrimary }}>Cancel</Button>
          <Button
            onClick={submitReject}
            variant="contained"
            disableElevation
            sx={{ textTransform: "none", fontWeight: 600, bgcolor: "error.main", "&:hover": { bgcolor: "error.dark" } }}
          >
            Reject request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ---------------------------------------------------------------- Shell ----------------------------------------------------------------

const TABS = [
  { value: "campaigns", label: "Campaigns", icon: <StorefrontOutlinedIcon fontSize="small" /> },
  { value: "profile", label: "Profile", icon: <PersonOutlineOutlinedIcon fontSize="small" /> },
  { value: "reports", label: "Reports", icon: <BarChartOutlinedIcon fontSize="small" /> },
  { value: "conversions", label: "Conversions", icon: <ReceiptLongOutlinedIcon fontSize="small" /> },
  { value: "brand-access", label: "Brand Access", icon: <PlaylistAddCheckOutlinedIcon fontSize="small" /> },
];

// Lives inside the Admin Dashboard (/brand/admin-dashboard/publisher — see
// dashboard.tsx's "Publisher" tab/sidebar entry), gated on both sides by the
// view_publisher_manager flag on user_roles (see migrations/user_roles/
// 2026-09-10_view_publisher_manager.sql): the frontend hides the tab unless
// UserData.view_publisher_manager === "True", and every
// admin-dashboard/publisher/* backend route independently re-checks the same
// flag via Roles.checkAccessAdminDashboard — this page shows unfiltered,
// account-wide financial data (every campaign, every influencer's
// performance broken out by the "source" dimension), so it must not be
// reachable just because someone guesses/bookmarks a URL. Account-wide view
// of the single shared Trackier publisher account.
export const PublisherManager: React.FC = () => {
  const [tab, setTab] = useState("campaigns");
  const userId = useSelector((state: any) => state.UserReducer?.UserData?.user_id);

  return (
    <Box sx={{ minHeight: "100%" }}>
      <Box>
        <AdminPageHead
          title="Publisher"
          subtitle="Account-wide view of the Trackier affiliate network — every campaign, the account's own profile, and unfiltered performance."
        />
        <AdminTabs
          current={tab}
          onChange={(v) => setTab(v as typeof tab)}
          tabs={TABS.map((t) => ({ value: t.value, label: t.label, icon: t.icon }))}
        />

        {tab === "campaigns" && <CampaignsPanel userId={userId} />}
        {tab === "profile" && <ProfilePanel userId={userId} />}
        {tab === "reports" && <ReportsPanel userId={userId} />}
        {tab === "conversions" && <ConversionsPanel userId={userId} />}
        {tab === "brand-access" && <BrandAccessPanel userId={userId} />}
      </Box>
    </Box>
  );
};

export default PublisherManager;
